<template>
  <div class="category-card-wrapper">
    <v-card class="category-card" elevation="0">
      <!-- Category Header (Clickable) -->
      <div
        class="category-card-header"
        @click="toggleExpanded"
      >
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-3">{{ getIcon(category.icon) }}</v-icon>
          <span class="text-h6">{{ category.name }}</span>
          <v-chip v-if="showDocCount && category.docs" size="small" color="primary" variant="tonal" class="ml-3">
            {{ category.docs.length }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-icon class="category-chevron">
            {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
          </v-icon>
        </div>
      </div>

      <!-- Category Content (Expandable) -->
      <v-expand-transition>
        <div v-show="isExpanded" class="category-card-content">
          <!-- Subcategories -->
          <div v-if="category.children && category.children.length > 0" class="subcategories">
            <v-chip
              v-for="child in category.children"
              :key="child.id"
              :href="child.path"
              link
              size="small"
              color="grey"
              variant="outlined"
              class="mr-2 mb-2"
            >
              <v-icon start size="small">mdi-folder-outline</v-icon>
              {{ child.name }}
            </v-chip>
          </div>

          <!-- Documents List -->
          <v-list v-if="category.docs && category.docs.length > 0" class="docs-list">
            <v-list-item
              v-for="doc in category.docs"
              :key="doc.id"
              :href="doc.path"
              class="doc-item"
            >
              <template v-slot:prepend>
                <v-icon size="small">mdi-file-document-outline</v-icon>
              </template>
              <v-list-item-title>{{ doc.title }}</v-list-item-title>

              <!-- Tags -->
              <template v-if="doc.tags && doc.tags.length > 0" v-slot:append>
                <div class="d-flex align-center">
                  <v-chip
                    v-for="tag in doc.tags.slice(0, 3)"
                    :key="tag.id"
                    size="small"
                    :style="{ backgroundColor: tagColor(tag.name), color: '#fff' }"
                    class="mr-1"
                  >
                    {{ tag.name }}
                  </v-chip>
                  <v-icon
                    v-if="doc.topics_count && doc.topics_count > 0"
                    size="small"
                    color="grey-lighten-1"
                  >
                    mdi-comment
                  </v-icon>
                </div>
              </template>
            </v-list-item>

            <!-- More Documents Link -->
            <v-list-item
              v-if="showMoreLink && category.docs.length >= 15"
              :href="category.path"
              class="more-link"
            >
              <template v-slot:prepend>
                <v-icon size="small">mdi-dots-horizontal</v-icon>
              </template>
              <v-list-item-title class="text-primary font-weight-medium">
                {{ t.more_documents }}
              </v-list-item-title>
            </v-list-item>
          </v-list>

          <!-- Empty State -->
          <div v-if="!category.docs || category.docs.length === 0" class="text-center py-8">
            <v-icon size="48" color="grey-lighten-1">mdi-file-document-outline</v-icon>
            <p class="text-body-2 mt-4 text-grey">{{ t.no_documents }}</p>
          </div>
        </div>
      </v-expand-transition>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'CategoryCard',
  props: {
    category: {
      type: Object,
      required: true
    },
    expanded: {
      type: Boolean,
      default: false
    },
    showDocCount: {
      type: Boolean,
      default: true
    },
    showMoreLink: {
      type: Boolean,
      default: true
    },
    translations: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      isExpanded: this.expanded
    }
  },
  computed: {
    t() {
      return {
        more_documents: this.translations.more_documents || 'More Documents',
        no_documents: this.translations.no_documents || 'No documents available'
      }
    }
  },
  methods: {
    getIcon(iconName) {
      if (!iconName) return 'mdi-folder'
      return iconName.startsWith('mdi-') ? iconName : `mdi-${iconName}`
    },
    tagColor(name) {
      const colors = {
        a: '#1abc9c', b: '#16a085', c: '#f1c40f', d: '#f39c12',
        e: '#2ecc71', f: '#27ae60', g: '#e67e22', h: '#d35400',
        i: '#3498db', j: '#2980b9', k: '#e74c3c', l: '#e74c3c',
        m: '#9b59b6', n: '#8e44ad', o: '#bdc3c7', p: '#34495e',
        q: '#2c3e50', r: '#95a5a6', s: '#7f8c8d', t: '#ec87bf',
        u: '#d870ad', v: '#f69785', w: '#9ba37e', x: '#b49255',
        y: '#b49255', z: '#a94136'
      }
      const letter = (name || '').charAt(0).toLowerCase()
      return colors[letter] || '#95a5a6'
    },
    toggleExpanded() {
      this.isExpanded = !this.isExpanded
    }
  }
}
</script>

<style scoped>
.category-card-wrapper {
  padding-top: 2px;
  margin-bottom: 16px;
}

.category-card {
  border: 1px solid rgb(206, 216, 224) !important;
  border-radius: 15px !important;
  background: white !important;
}

.category-card-header {
  padding: 16px 20px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #e0e0e0;
}

.category-card-header:hover {
  background-color: #f5f5f5;
}

.category-chevron {
  transition: transform 0.2s ease;
}

.category-card-content {
  padding: 20px;
}

.subcategories {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  padding-top: 8px;
}

.docs-list {
  background: transparent !important;
  padding: 0;
}

.doc-item {
  padding: 4px 16px;
  min-height: 36px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.15s ease;
}

.doc-item:last-child {
  border-bottom: none;
}

.doc-item:hover {
  background-color: #f5f5f5;
}

.doc-item :deep(.v-list-item__prepend) {
  padding-top: 12px;
  padding-bottom: 12px;
}

.more-link {
  color: var(--v-theme-primary);
  font-weight: 500;
}

.more-link:hover {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Remove underline from all links */
a {
  text-decoration: none;
}

a:hover {
  text-decoration: none !important;
}

/* Responsive adjustments */
@media (max-width: 599px) {
  .category-card-wrapper {
    margin-bottom: 12px;
  }

  .category-card-header {
    padding: 12px 16px;
  }

  .category-card-content {
    padding: 16px;
  }

  .doc-item {
    padding: 12px;
  }
}
</style>
